// ClosedMailQueue (2026-11-04, Lyndon): mail on a closed ticket waits here for a scheduler. Shown on every page for office roles.
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, TextField, Typography } from '@mui/material';
import api from '@/services/api';
import { useWorkspace } from '@/contexts/WorkspaceContext';

const OFFICE = ['SuperAdmin', 'Admin', 'Dispatcher', 'Scheduler'];

export default function ClosedMailQueue() {
  const { ws } = useWorkspace() as any;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [rows, setRows] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [msg, setMsg] = useState('');
  const active = ws?.active_role?.name;
  const office = !active || OFFICE.includes(active);
  const load = useCallback(() => {
    if (!office) { setRows([]); return; }
    api.get('/tech-app/closed-emails').then((r: any) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => setRows([]));
  }, [office]);
  useEffect(() => { load(); const h = setInterval(load, 30000); return () => clearInterval(h); }, [load]);
  useEffect(() => { if (open && !rows.length) setOpen(false); }, [open, rows.length]);

  const act = async (id: number, action: string) => {
    setMsg('');
    try {
      const r: any = await api.post(`/tech-app/closed-emails/${id}/action`, { action, note: notes[id] || null });
      if (r.data?.ticket_number) setMsg(`Reopened as ${r.data.ticket_number}`);
      load();
    } catch (e: any) { setMsg(e?.response?.data?.detail || 'That did not save. Try again.'); }
  };
  const openTicket = (id: number) => { setOpen(false); navigate(`${pathname.startsWith('/tech') ? '/tech/tickets' : '/tickets'}?id=${id}`); };

  if (!rows.length) return msg ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert> : null;
  return (
    <>
      <Alert severity="warning" variant="filled" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => setOpen(true)}>Decide</Button>}>
        {rows.length} email{rows.length === 1 ? '' : 's'} on closed tickets need a scheduler decision
      </Alert>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Email on closed tickets</DialogTitle>
        <DialogContent dividers>
          {msg ? <Alert severity="info" sx={{ mb: 2 }}>{msg}</Alert> : null}
          {rows.map((e, i) => (
            <Box key={e.id}>
              {i > 0 ? <Divider sx={{ my: 2 }} /> : null}
              <Typography sx={{ fontWeight: 700 }}>{e.ticket_number} · {e.customer_name}</Typography>
              <Typography variant="body2">{e.from_name ? `${e.from_name} <${e.from_email}>` : e.from_email}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{e.subject || '(no subject)'}</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: 160, overflow: 'auto', mt: 0.5 }}>{(e.body_text || '').slice(0, 1500)}</Typography>
              <TextField size="small" fullWidth sx={{ mt: 1.5 }} label="Note (optional)" value={notes[e.id] || ''} onChange={(x) => setNotes({ ...notes, [e.id]: x.target.value })} />
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                <Button size="small" variant="contained" onClick={() => act(e.id, 'reopen')}>Reopen as new ticket</Button>
                <Button size="small" variant="outlined" onClick={() => act(e.id, 'file')}>No action, keep on file</Button>
                <Button size="small" variant="outlined" onClick={() => act(e.id, 'dead')}>Ticket is dead, tell sender</Button>
                <Button size="small" variant="outlined" color="error" onClick={() => act(e.id, 'ban')}>Not the customer, ban sender</Button>
                <Button size="small" onClick={() => openTicket(e.ticket_id)}>Open ticket</Button>
              </Stack>
            </Box>
          ))}
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </>
  );
}
